import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import {
  HD_LOVE_CHART_ASSET,
  HD_LOVE_MANDALA_RING_ASSET,
} from '@/app/human-design/chart/love/hd-love.metadata';

/**
 * Pagina met de Love gates cheat sheet ("Human Design Connectiviteitspoorten"),
 * nagebouwd uit het Figma-design (node 1732-8232, A3, zonder branding-footer).
 * Drie doorlopende kolommen zoals op het poster: links de algemene tekst en de
 * kaarten 10/15/44/28 + Ra-quote + 58, centraal de mandala-ring, de
 * reflectievragen en de bodygraph-fragmentenchart met de love-dots, rechts de
 * twee poortgroepen (Vessel of Love / Alledaagse) en de kaarten 25/46/40/55 +
 * Ra-quote + 41.
 *
 * Alle tekst komt uit de `human-design/love`-scope (taalwissel + NL-fallback);
 * dezelfde scope voedt het love-gates-paneel en de connectiviteitspoorten-
 * sectie bij composites. Printbaar op één A4-blad (screen-scoped responsive
 * queries + zoom, zoals de conditionering-cheat-sheet).
 */
@Component({
  selector: 'hd-love-gates-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-love-gates-cheat-sheet-page.component.html',
  styleUrl: './hd-love-gates-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLoveGatesCheatSheetPageComponent {
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly gateInfo = inject(HdGateInfoService);

  protected readonly mandalaRingAsset = HD_LOVE_MANDALA_RING_ASSET;
  protected readonly chartAsset = HD_LOVE_CHART_ASSET;

  /** Kaartvolgorde per kolom, zoals op het Figma-poster (boven → onder). */
  protected readonly leftTopGates: readonly number[] = [10, 15, 44, 28];
  protected readonly leftBottomGate = 58;
  protected readonly rightTopGates: readonly number[] = [25, 46, 40, 55];
  protected readonly rightBottomGate = 41;

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly title = computed(() => this.text('title'));
  protected readonly titleSuffix = computed(() => this.text('titleSuffix'));
  protected readonly generalHeading = computed(() => this.text('general.heading'));
  protected readonly generalParagraphs = computed(() => this.list('general.paragraphs'));
  protected readonly generalQuote = computed(() => this.text('general.quote'));
  protected readonly generalQuoteAuthor = computed(() => this.text('general.quoteAuthor'));
  protected readonly generalParagraphs2 = computed(() => this.list('general.paragraphs2'));

  protected readonly groups = computed(() =>
    (['vessel', 'everyday'] as const).map(key => ({
      key,
      gates: this.text(`groups.${key}.gates`),
      title: this.text(`groups.${key}.title`),
      lead: this.text(`groups.${key}.lead`),
      paragraphs: this.list(`groups.${key}.paragraphs`),
    })),
  );

  protected readonly reflectionTitle = computed(() => this.text('reflection.title'));
  protected readonly reflectionQuestions = computed(() => this.list('reflection.questions'));
  protected readonly quoteLeft = computed(() => this.text('quotes.left'));
  protected readonly quoteRight = computed(() => this.text('quotes.right'));

  constructor() {
    // Beide content-talen eager laden (idempotent per taal) zodat de taalwissel
    // én de NL-fallback meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /** Love-scope tekst met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('love', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('love', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetListIn('love', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('love', key, 'nl');
  }
}
